import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ROW_COLORS } from '@/lib/constants';
import { Search, AlertTriangle, X } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Searchable slot picker — replaces manual text input for slot addresses.
 * Shows all 764 slots, filterable by typing. Colour-coded by row.
 * Empty slots listed first, full slots greyed out and non-selectable.
 */
export function SlotPicker({ value, onChange, disabled, placeholder = "Search slot...", className = "" }) {
  const [slots, setSlots] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch slots on mount
  useEffect(() => {
    const fetchSlots = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('wms_token');
        const res = await fetch(`${API_BASE}/api/slots/picker`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setSlots(data.slots || []);
      } catch (err) {
        console.error('Failed to load slots', err);
      }
      setLoading(false);
    };
    fetchSlots();
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return slots;
    const q = search.trim().toUpperCase();
    return slots.filter(s =>
      s.slot_address.toUpperCase().includes(q) ||
      s.row.toUpperCase().includes(q) ||
      s.rack_code.toUpperCase().includes(q) ||
      `L${s.level}`.includes(q)
    );
  }, [slots, search]);

  const handleSelect = (slot) => {
    if (slot.status === 'full') return;
    onChange(slot.slot_address);
    setSearch('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setSearch('');
    inputRef.current?.focus();
  };

  const statusBadge = (status) => {
    const map = {
      empty: { label: 'Empty', bg: '#22c55e', text: 'white' },
      occupied: { label: 'Occupied', bg: '#3b82f6', text: 'white' },
      qc_hold: { label: 'QC Hold', bg: '#e8191a', text: 'white' },
      full: { label: 'Full', bg: '#9ca3af', text: 'white' },
    };
    const s = map[status] || map.empty;
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: s.bg, color: s.text }}>
        {s.label}
      </span>
    );
  };

  // Selected chip display
  if (value && !open) {
    const selectedSlot = slots.find(s => s.slot_address === value);
    const row = value[0] || 'A';
    return (
      <div className={`relative ${className}`} ref={wrapperRef}>
        <div
          className="flex items-center gap-2 h-10 px-3 border rounded-md bg-white cursor-pointer"
          onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
          data-testid="slot-picker-chip"
        >
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: ROW_COLORS[row] || '#6b7280' }} />
          <span className="font-mono font-bold text-sm" style={{ color: ROW_COLORS[row] || '#333' }}>{value}</span>
          {selectedSlot && statusBadge(selectedSlot.status)}
          <button onClick={(e) => { e.stopPropagation(); handleClear(); }} className="ml-auto p-0.5 hover:bg-gray-100 rounded">
            <X size={14} className="text-gray-400" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-3 text-gray-400" />
        <Input
          ref={inputRef}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={loading ? "Loading slots..." : placeholder}
          className="h-10 pl-9 text-sm"
          disabled={disabled || loading}
          data-testid="slot-picker-input"
        />
      </div>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
          data-testid="slot-picker-dropdown"
        >
          {filtered.length === 0 ? (
            <div className="p-3 text-sm text-gray-400 text-center">No slots found</div>
          ) : (
            filtered.slice(0, 50).map((slot) => {
              const isFull = slot.status === 'full';
              return (
                <button
                  key={slot.slot_address}
                  onClick={() => handleSelect(slot)}
                  disabled={isFull}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors min-h-[44px] ${
                    isFull ? 'opacity-40 cursor-not-allowed bg-gray-50' : 'hover:bg-blue-50 cursor-pointer'
                  }`}
                  data-testid={`slot-option-${slot.slot_address}`}
                >
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: ROW_COLORS[slot.row] || '#6b7280' }} />
                  <span className="font-mono font-bold text-sm flex-1" style={{ color: ROW_COLORS[slot.row] || '#333' }}>
                    {slot.slot_address}
                  </span>
                  {slot.is_conflict && <AlertTriangle size={14} className="text-amber-500" />}
                  {statusBadge(slot.status)}
                </button>
              );
            })
          )}
          {filtered.length > 50 && (
            <div className="p-2 text-xs text-center text-gray-400">Type more to narrow results ({filtered.length} total)</div>
          )}
        </div>
      )}
    </div>
  );
}
